import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  colors,
  Divider,
  IconButton,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../redux";
import { Notification, useDeleteAllNotificationMutation, useDeleteNotificationMutation, useGetNotificationsQuery, useUpdateNotificationMutation } from "../../redux/services/notifications";
import { closeDrawer } from "../../redux/slices/drawer";
import { AppRoutes } from "../../router/routes";
import { NotificationCard } from "./components/Notification";
import { MyPaper } from "../myPaper";
import { DeleteIcon } from "../../images/svg/deleteIcon";
import { useEffect, useState, FC } from "react";
import { ReadAllIcon } from "../../images/svg/readAllIcon";
import { enqueueSnackbar } from "notistack";
import { closeDialog } from "../../redux/slices/dialog";
import { closeConfirmationDialog, openConfirmationDialog } from "../../redux/slices/confirmationDialog";
import { createOption } from "../keywordSearch";

interface INotificationRenderer {
  isLoading: boolean,
  checkboxProps?: CheckboxProps,
  notifications: NotificationExtended[],
  refetch: () => void
}

export type CheckboxProps = {
  include: boolean;
  checkboxHandler: (id: string) => void;
};

export type NotificationExtended = Notification & { include?: boolean };

export const severity: any = {
  completed: "success",
  saved: "success",
  "In Progress": "info",
  "notUploaded": "info",
  "pending": "info",
  failed: "error",
};

const defaultPagination = { page_no: 1, page_size: 50 };

const fullPagination = { page_no: 1, page_size: 5000 };

const NotificationRenderer: FC<INotificationRenderer> = ({ isLoading, checkboxProps, notifications, refetch }) => {

  return (
    <Stack flexGrow={1} overflow={"auto"} gap={isLoading ? 2 : 0}>
      {isLoading ? (
        [1, 2, 3, 4, 5].map((key) => (
          <Skeleton key={key} variant="rounded" width="100%" height="46px" />
        ))
      ) : (
        notifications.length === 0
          ? <Box sx={{ p: 2 }}>
            <Alert
              severity="info"
            >
              No new notifications at the moment.
            </Alert>
          </Box>
          : notifications.map((notification: NotificationExtended) => (
            <NotificationCard
              key={notification?._id}
              notification={notification}
              refetch={refetch}
              checkboxProps={checkboxProps}
            />
          ))
      )}
    </Stack>
  );
};

export const NotificationsMenu = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { data: allData = { data: [], totalCount: 0 }, isLoading, refetch } =
    useGetNotificationsQuery(defaultPagination, {
      pollingInterval: 5000,
    })

  const { data: notifications } = allData;

  return (
    <Stack
      sx={{
        p: 1,
        px: 2,
        overflow: "auto",
        height: "calc(100vh - 70px)",
      }}
    >
      <Stack gap={1} height={"100%"} overflow={"hidden"}>
        <NotificationRenderer notifications={notifications} refetch={refetch} isLoading={isLoading} />

        <Button
          sx={{ mt: "auto" }}
          onClick={() => {
            navigate(AppRoutes.notifications);
            dispatch(closeDrawer())
          }}
        >View More</Button>
      </Stack>
    </Stack>

  );
}

const allTabs = [
  createOption("All"),
  createOption("Unread"), 
];

export const NotificationsPage = () => {
  const dispatch = useAppDispatch();

  const [currentTab, setCurrentTab] = useState(allTabs[0].value);

  const { data: allData = { data: [], totalCount: 0 }, isLoading, refetch } =
    useGetNotificationsQuery({...fullPagination, status: currentTab === allTabs[1].value ? "unread" : undefined}, {
      pollingInterval: 5000,
    })

  const { data } = allData;

  const [markAsRead, readResponse] = useUpdateNotificationMutation();
  const [deleteAllNotifications, deleteAllResponse] = useDeleteAllNotificationMutation();
  const [deleteNotifications, deleteResponse] = useDeleteNotificationMutation();

  const [notifications, setNotifications] = useState<NotificationExtended[]>([]);
  const [selectedAll, setSelectAll] = useState(false);

  const checkboxHandler = (id: string) => {
    let includeCount = 0;
    setNotifications((prevState) => prevState.map((p) => {
      if (id === p?._id) {
        const isChecked = !p.include;
        if (isChecked) {
          includeCount++
          setSelectAll(true);
        };
        return ({ ...p, include: isChecked })
      }
      if (p.include) includeCount++;
      return p
    }));
    if (includeCount === 0) {
      setSelectAll(false);
    }
  };

  const updateSelectedState = (data: Notification[], checked: boolean) => {
    setNotifications(data.map(d => ({ ...d, include: checked })))
  };

  const handleDeleteAll = async () => {
    try {
      await deleteAllNotifications({}).unwrap();
      setSelectAll(false);
      refetch();
    } catch (error: any) {
      let errorMessage: string = error?.data?.title ?? `Unable to delete the notifications!`;
      enqueueSnackbar(errorMessage, { variant: "error" });
    } finally {
      dispatch(closeDialog());
    }
  }

  const deleteSelectedNotifications = async (n: NotificationExtended[]) => {
    await Promise.all(n.map(n => deleteNotifications(n?._id)))
      .then(res => {
        const errorRes: any[] = res?.filter((r: any) => r?.error) ?? [];
        if (errorRes?.length > 0) {
          enqueueSnackbar(`Unable to delete the selected notifications. Info: ${errorRes[0]?.error?.data?.title ?? errorRes[0]?.error?.data ?? "Unknown"}`, { variant: "error" })
        } else {
          enqueueSnackbar(`Selected Notifications deleted successfully!!!`, { variant: "success" });
        }
        setSelectAll(false);
        refetch();
      }).catch((err) => {
        enqueueSnackbar(`Unable to delete the selected notifications. Info: ${err?.data?.title ?? "Unknown"}`, { variant: "warning" })
      });
  };

  const initiateDeleteNotifications = (n: NotificationExtended[]) => {
    const filteredNotifications = n.filter(n => n?.include);
    dispatch(
      openConfirmationDialog({
        title: "Delete Operation",
        body: `Are you sure you want to delete all the notifications?`,
        positiveBtn: "Delete All",
        negativeBtn: "Cancel",
        onOk: () => {
          if (filteredNotifications?.length === n?.length) {
            handleDeleteAll();
          } else {
            deleteSelectedNotifications(filteredNotifications);
          }
          dispatch(closeConfirmationDialog());
        },
        onNegativeBtn: () => dispatch(closeConfirmationDialog()),
      })
    );
  };

  const onMarkStatus = async (n: NotificationExtended[]) => {
    await Promise.all(n.filter(n => n?.include).map(n => markAsRead({ id: n?._id, status: "read" })))
      .then(res => {
        const errorRes: any[] = res?.filter((r: any) => r?.error) ?? [];
        if (errorRes?.length > 0) {
          enqueueSnackbar(`Unable to mark the selected notification as read. Info: ${errorRes[0]?.error?.data?.title ?? errorRes[0]?.error?.data ?? "Unknown"}`, { variant: "error" })
        } else {
          enqueueSnackbar(`Selected Notifications marked as read!!!`, { variant: "success" });
        }
        setSelectAll(false);
        refetch();
      }).catch((err) => {
        enqueueSnackbar(`Unable to mark the selected notification as read. Info: ${err?.data?.title ?? "Unknown"}`, { variant: "warning" })
      });
  };

  useEffect(() => {
    updateSelectedState(data ?? [], false)
  }, [data])
  
  useEffect(() => {
   refetch();
   // eslint-disable-next-line
  }, [currentTab])

  return (
    <Stack
      height={"100%"}
      gap={3}
      sx={{
        p: 1,
        px: 2,
      }}
    >
      <Typography variant="h6" sx={{ fontFamily: "htrts_semibold", pl: 0.5 }}>Notifications</Typography>
      <MyPaper height={"100%"} padding={0}>
        <Stack height={"100%"}>
          <Tabs
            value={currentTab}
            onChange={(e, tab) => setCurrentTab(tab)}
            variant="scrollable"
            scrollButtons="auto"
            indicatorColor="primary"
            textColor="primary"
          >
            {allTabs.map(tab => <Tab key={tab.value as any} sx={{ fontSize: 14 }} value={tab.value} label={tab.label} wrapped />)}
          </Tabs>
          <Stack sx={{ p: 2 }} alignItems={"center"} gap={1} direction={"row"}>
            <Tooltip title={"Select All"}>
              <Checkbox
                size="small"
                checked={selectedAll}
                disabled={notifications.length === 0}
                onChange={() => {
                  setSelectAll(prevState => !prevState);
                  updateSelectedState(notifications, selectedAll ? false : true)
                }}
              />
            </Tooltip>

            <Divider
              orientation="vertical"
              flexItem
              sx={{
                height: 24,
                my: 0.5,
                borderColor: 'rgba(0, 0, 0, 0.2)'
              }}
            />
            <Tooltip title="Mark all as read">
              {readResponse.isLoading
                ? <CircularProgress size={20} />
                : <IconButton disabled={!selectedAll || deleteResponse.isLoading || deleteAllResponse.isLoading} size="small" onClick={() => onMarkStatus(notifications)}>
                  <ReadAllIcon color={selectedAll && !(deleteResponse.isLoading || deleteAllResponse.isLoading) ? undefined : colors.grey[500]} />
                </IconButton>
              }
            </Tooltip>

            <Divider
              orientation="vertical"
              flexItem
              sx={{
                height: 24,
                my: 0.5,
                borderColor: 'rgba(0, 0, 0, 0.2)'
              }}
            />

            {deleteResponse.isLoading || deleteAllResponse.isLoading ? <CircularProgress size={20} /> :
              <IconButton disabled={!selectedAll || readResponse.isLoading} size="small" onClick={() => initiateDeleteNotifications(notifications)}>
                <DeleteIcon color={(selectedAll && !readResponse.isLoading) ? undefined : colors.grey[500]} />
              </IconButton>}
          </Stack>
          <NotificationRenderer notifications={notifications} refetch={refetch} isLoading={isLoading} checkboxProps={{ include: true, checkboxHandler }} />
        </Stack>
      </MyPaper>
    </Stack>
  );
}


